import React from 'react';
import { ManagerRow } from './ManagerRow';
import { CompanySplitRows } from './CompanySplitRows';

type RevenueTableProps = {
  isLoading: boolean;
  error?: string;
  sortedRows: any[];
  statuses: string[];
  gridStyle: any;
  expandedManagers: Set<string>;
  setExpandedManagers: (value: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  hasCompanySplit: (manager: string) => boolean;
  getCompanySplit: (manager: string) => any[];
  hasHotelSplit?: (manager: string, company: string) => boolean;
  getHotelSplit?: (manager: string, company: string) => any[];
};

const managerColumnWidth = '5.5rem';
const nbProjectsWidth = '5.5rem';
const realisedRevenueWidth = '6.5rem';
const groupsColumnsWidth = 'minmax(40px, 1fr)';
const otherColumnsWidth = `minmax(60px, 1fr)`;
const materializationWidth = '5.5rem';

function RevenueTableHeader({ statuses, gridStyle }: { statuses: string[], gridStyle: any }) {
  const groupColSpan = statuses.length + 1;
  return (
    <div className="bg-gray-50">
      <div
        className="gap-2 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider grid"
        style={gridStyle}
      >
        <div className="flex items-center w-16 justify-center">Mgr</div>
        <div className="flex items-center justify-center" style={{ minWidth: nbProjectsWidth }}>PROJECTS</div>
        <div className="flex items-center justify-center" style={{ gridColumn: `span ${groupColSpan}` }}>GROUPS</div>
        <div className="flex items-center justify-center" style={{ gridColumn: `span ${groupColSpan}` }}>ESTIMATED REVENUE</div>
        <div className="flex items-center justify-center" style={{ gridColumn: `span 2`, minWidth: `calc(${realisedRevenueWidth} + ${materializationWidth})` }}>FINAL REVENUE</div>
      </div>
      <div
        className="gap-2 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider grid"
        style={gridStyle}
      >
        <div />
        <div className="text-center" style={{ fontSize: "10px", padding: "0.5rem 0.25rem", minWidth: "60px", wordBreak: "break-all" }}>TOTAL</div>
        {statuses.map(status => (
          <div key={"nb-"+status} className="text-center" style={{ fontSize: "10px", padding: "0.5rem 0.25rem", minWidth: "60px", wordBreak: "break-all" }}>
            {status === "Not launched" ? "NOT L." : status.toUpperCase()}
          </div>
        ))}
        <div className="text-center" style={{ fontSize: "10px", padding: "0.5rem 0.25rem", minWidth: "60px", wordBreak: "break-all" }}>TOTAL</div>
        {statuses.map(status => (
          <div key={"est-"+status} className="text-center" style={{ fontSize: "10px", padding: "0.5rem 0.25rem", minWidth: "60px", wordBreak: "break-all" }}>
            {status === "Not launched" ? "NOT L." : status.toUpperCase()}
          </div>
        ))}
        <div className="text-center" style={{ fontSize: "10px", padding: "0.5rem 0.25rem", minWidth: "60px", wordBreak: "break-all" }}>TOTAL</div>
        <div className="text-center" style={{ fontSize: "10px", padding: "0.5rem 0.25rem", minWidth: realisedRevenueWidth, wordBreak: "break-all" }}>TOTAL</div>
        <div className="text-center" style={{ fontSize: "10px", padding: "0.5rem 0.25rem", minWidth: materializationWidth, wordBreak: "break-all" }}>MATER.</div>
      </div>
    </div>
  );
}

export function RevenueTable({
  isLoading,
  error,
  sortedRows,
  statuses,
  gridStyle: gridStyleProp,
  expandedManagers,
  setExpandedManagers,
  hasCompanySplit,
  getCompanySplit,
  hasHotelSplit = () => false,
  getHotelSplit = () => [],
}: RevenueTableProps) {
  const columnCount = statuses.length + 1;
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: [
      managerColumnWidth,
      nbProjectsWidth,
      ...Array(statuses.length + 1).fill(groupsColumnsWidth),
      ...Array(statuses.length + 1).fill(otherColumnsWidth),
      realisedRevenueWidth,
      materializationWidth
    ].join(' '),
    alignItems: 'center'
  };
  const [expandedCompanies, setExpandedCompanies] = React.useState<Set<string>>(new Set());
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="min-w-full divide-y divide-gray-200">
        <RevenueTableHeader statuses={statuses} gridStyle={gridStyle} />
        <div className="bg-white divide-y divide-gray-200">
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : error ? (
            <div className="text-red-500 p-4 text-center">{error}</div>
          ) : (
            sortedRows.map(row => (
              <React.Fragment key={row.manager}>
                <ManagerRow
                  row={row}
                  statuses={statuses}
                  gridStyle={gridStyle}
                  onToggle={() => {
                    if (hasCompanySplit(row.manager)) {
                      setExpandedManagers(prev => {
                        const next = new Set(prev);
                        if (next.has(row.manager)) next.delete(row.manager);
                        else next.add(row.manager);
                        return next;
                      });
                    }
                  }}
                  hasCompanySplit={hasCompanySplit(row.manager)}
                />
                {expandedManagers.has(row.manager) && (
                  <CompanySplitRows
                    companies={getCompanySplit(row.manager)}
                    statuses={statuses}
                    gridStyle={gridStyle}
                    expandedCompanies={expandedCompanies}
                    setExpandedCompanies={setExpandedCompanies}
                    hasHotelSplit={hasHotelSplit}
                    getHotelSplit={getHotelSplit}
                  />
                )}
              </React.Fragment>
            ))
          )}
        </div>
      </div>
    </div>
  );
} 